/**
 * Backups tab (Spec 201 — plan-centric model)
 *
 * Operational management of EXISTING backups. The "Backups" table manages each protected entity in
 * place — Back up now, adjust start time, and "Change plan" (move it to a different plan; its
 * schedule/retention/destination follow). Assigning WHICH entities a plan protects (and protecting
 * new ones) lives on the Management tab. Built from @/components/ui.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 201-backup-management-overhaul
 */

import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Play, Clock, X, RefreshCw, ArrowLeftRight } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardBody,
  DataTable,
  Button,
  Badge,
  Input,
  Select,
  Checkbox,
  Modal,
  EmptyState,
  LoadingState,
  ErrorState,
} from '@/components/ui';
import { useAuth } from '../../hooks/useAuth';
import { assignmentApi, AssignableEntity, BackupEntityType, BackupConfig } from '../../api/assignment';
import { retentionApi, GfsRetentionPolicy } from '../../api/retention';
import { tiersApi } from '../../api/tiers';
import { ProtectionTierBadge, PROTECTION_TIERS, tierIcon } from '../common/ProtectionTierBadge';
import { plansApi } from '../../api/plans';
import { ListFilterBar, textMatch } from '../common/ListFilterBar';
import { useTriggerBackup } from '../../hooks/useTriggerBackup';
import { ScheduleSpreadHeatmap } from '../schedules/ScheduleSpreadHeatmap';
import { BulkStorageRpoActions } from './BulkStorageRpoActions';
import { formatEastern } from '@/utils/dateFormatter';
import { storageAccountParts } from '../../utils/storageAccountLabel';

const fmtWhen = (iso: string | null | undefined): string =>
  iso ? formatEastern(iso) : '—';

/** Short-hand of what a retention policy MEANS (its GFS rules) — for the read-only Backups list. */
const retentionShort = (p: GfsRetentionPolicy | undefined): string => {
  if (!p) return '—';
  const parts: string[] = [];
  if (p.keepFull != null) parts.push(`${p.keepFull} full`);
  if (p.keepIncremental != null) parts.push(`${p.keepIncremental} incr`);
  const gfs = [
    p.gfsDaily != null ? `${p.gfsDaily}d` : null,
    p.gfsWeekly != null ? `${p.gfsWeekly}w` : null,
    p.gfsMonthly != null ? `${p.gfsMonthly}mo` : null,
    p.gfsYearly != null ? `${p.gfsYearly}y` : null,
  ].filter(Boolean);
  if (gfs.length) parts.push(gfs.join('/'));
  if (p.graceDays != null) parts.push(`${p.graceDays}d grace`);
  return parts.join(' · ') || 'no rules';
};

/** Search assigned entities across name / type / workspace / destination. */
const filterAssigned = (data: AssignableEntity[], q: string): AssignableEntity[] =>
  data.filter((e) => textMatch(q, e.name, e.entityType, e.workspaceName, e.storageAccountName));

const key = (e: { entityType: BackupEntityType; entityId: string }) => `${e.entityType}:${e.entityId}`;

const envVariant: Record<string, 'danger' | 'info' | 'warning' | 'default'> = {
  prod: 'danger',
  dev: 'info',
  uat: 'warning',
  test: 'default',
};
/** Inline START-TIME editor — the operator's one lever to react to the heatmap. Shows the plan's
 *  cadence + an editable HH:MM (the per-entity schedule the executor runs); saving moves the backup's
 *  cells on the heatmap above. */
const StartTimeCell: React.FC<{
  cfg: BackupConfig | undefined;
  disabled: boolean;
  onSave: (hour: number, minute: number) => Promise<void>;
}> = ({ cfg, disabled, onSave }) => {
  const [saving, setSaving] = useState(false);
  if (!cfg?.hasSchedule || cfg.startHour == null) {
    return <span className="text-xs text-[var(--color-text-secondary)]">No schedule</span>;
  }
  const val = `${String(cfg.startHour).padStart(2, '0')}:${String(cfg.startMinute ?? 0).padStart(2, '0')}`;
  const cadenceLabel = cfg.cadence ? cfg.cadence.replace(/\s*at\s+\d{1,2}:\d{2}.*$/i, '').trim() : 'Scheduled';
  // Spec 214 T063: commit on BLUR (or Enter), not per keystroke — editing hour then minute
  // used to fire two PUTs, each rewriting all of the entity's crons + next_run_at.
  const commit = async (value: string) => {
    const [h, m] = value.split(':').map(Number);
    if (!Number.isFinite(h)) return;
    if (h === cfg.startHour && (m || 0) === (cfg.startMinute ?? 0)) return; // unchanged
    setSaving(true);
    try {
      await onSave(h, m || 0);
    } finally {
      setSaving(false);
    }
  };
  return (
    <span className="block">
      <span className="block text-sm text-[var(--color-text-primary)]">{cadenceLabel}</span>
      <span className="mt-1 flex items-center gap-2">
        <Input
          type="time"
          defaultValue={val}
          key={val}
          disabled={disabled || saving}
          className="w-36 text-left"
          onBlur={(e) => void commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void commit((e.target as HTMLInputElement).value);
          }}
        />
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--color-accent)]" />}
      </span>
      {cfg.nextRunAt && <span className="mt-0.5 block text-xs text-[var(--color-text-secondary)]">next {fmtWhen(cfg.nextRunAt)}</span>}
    </span>
  );
};

export const StorageAssignment: React.FC = () => {
  const { hasRole } = useAuth();
  const canManage = hasRole(['admin', 'operator']);
  const [asgQ, setAsgQ] = useState('');
  const [asgType, setAsgType] = useState('');
  const [asgEnv, setAsgEnv] = useState('');
  const [asgTier, setAsgTier] = useState('');
  const [asgDest, setAsgDest] = useState('');
  const [asgLast, setAsgLast] = useState(''); // '' | 'never' | 'backed'
  const [heatHour, setHeatHour] = useState<number | null>(null); // heatmap-square drill-down (by hour)
  const qc = useQueryClient();

  const entities = useQuery({ queryKey: ['backup', 'assignable-entities'], queryFn: assignmentApi.assignableEntities });
  const storage = useQuery({ queryKey: ['backup', 'storage-accounts'], queryFn: assignmentApi.storageAccounts });
  const plansQ = useQuery({ queryKey: ['backup', 'plans'], queryFn: plansApi.list });
  const planById = useMemo(() => new Map((plansQ.data ?? []).map((p) => [p.id, p])), [plansQ.data]);

  const [busy, setBusy] = useState(false);
  const [changeFor, setChangeFor] = useState<AssignableEntity | null>(null);
  const [trigger, setTrigger] = useState<Record<string, { status: 'running' | 'launched' | 'failed'; msg?: string }>>({});

  const assigned = useMemo(() => (entities.data ?? []).filter((e) => e.assigned), [entities.data]);
  const storageName = (id: string) => storage.data?.find((s) => s.id === id)?.name ?? id;

  const { trigger: triggerBackup } = useTriggerBackup();

  // Per-backup config (governing plan's schedule/cadence + last/next) keyed by entity.
  const configsQ = useQuery({ queryKey: ['backup', 'backup-configs'], queryFn: assignmentApi.backupConfigs });
  const configBy = useMemo(() => {
    const m = new Map<string, BackupConfig>();
    for (const c of configsQ.data ?? []) m.set(`${c.entityType}:${c.entityId}`.toLowerCase(), c);
    return m;
  }, [configsQ.data]);
  const cfg = (e: AssignableEntity) => configBy.get(key(e).toLowerCase());

  // Retention policy rules (for the read-only short-hand summary on the list).
  const retentionQ = useQuery({ queryKey: ['backup', 'retention-policies'], queryFn: retentionApi.listPolicies });
  const policyById = useMemo(() => new Map((retentionQ.data ?? []).map((p) => [p.id, p])), [retentionQ.data]);

  // Distinct destinations across the assigned set — for the destination filter.
  const destOptions = useMemo(() => {
    const s = new Set<string>();
    assigned.forEach((e) => {
      const n = e.storageAccountName ?? e.storageAccountId;
      if (n) s.add(n);
    });
    return [...s].sort();
  }, [assigned]);

  // Search + filters (type / env / tier / destination / last-backup) + heatmap-hour drill-down.
  const shownAssigned = useMemo(() => {
    let list = filterAssigned(assigned, asgQ);
    if (asgType) list = list.filter((e) => e.entityType === asgType);
    if (asgEnv) list = list.filter((e) => e.envHint === asgEnv);
    if (asgTier) list = list.filter((e) => (configBy.get(key(e).toLowerCase())?.protectionTier ?? '') === asgTier);
    if (asgDest) list = list.filter((e) => (e.storageAccountName ?? e.storageAccountId) === asgDest);
    if (asgLast)
      list = list.filter((e) => {
        const has = !!configBy.get(key(e).toLowerCase())?.lastBackupAt;
        return asgLast === 'backed' ? has : !has;
      });
    if (heatHour != null) list = list.filter((e) => (configBy.get(key(e).toLowerCase())?.scheduleHours ?? []).includes(heatHour));
    return list;
  }, [assigned, asgQ, asgType, asgEnv, asgTier, asgDest, asgLast, heatHour, configBy]);

  // Spec 208 — explicit multi-select (like the Plans tab) drives the bulk reassign / RPO actions.
  // Select rows → a bulk bar appears; the actions operate on the SELECTED set (not the filter).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggleSel = (k: string) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  // Workspace rows are not storage-assignable → exclude them from select-all + the bulk actions.
  const selectableShown = shownAssigned.filter((e) => e.entityType !== 'workspace');
  const allShownSelected = selectableShown.length > 0 && selectableShown.every((e) => selected.has(key(e)));
  const toggleAllShown = () =>
    setSelected((s) => {
      const n = new Set(s);
      if (allShownSelected) selectableShown.forEach((e) => n.delete(key(e)));
      else selectableShown.forEach((e) => n.add(key(e)));
      return n;
    });
  const selectedTargets = useMemo(
    () =>
      [...selected].map((k) => {
        const i = k.indexOf(':');
        return { entityType: k.slice(0, i) as BackupEntityType, entityId: k.slice(i + 1) };
      }),
    [selected]
  );

  const refreshSchedules = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['backup', 'backup-configs'] }),
      qc.invalidateQueries({ queryKey: ['backup', 'schedule-spread'] }),
    ]);

  // Manual refresh — re-fetch the list (last backup, status, schedule) after a backup runs.
  const refreshing = configsQ.isFetching || entities.isFetching;
  const refreshBackups = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['backup', 'backup-configs'] }),
      qc.invalidateQueries({ queryKey: ['backup', 'assignable-entities'] }),
      qc.invalidateQueries({ queryKey: ['backup', 'schedule-spread'] }),
    ]);

  // Reset all Backups-list filters (search + selects + heatmap-hour drill-down) in one click.
  const filtersActive = !!(asgQ || asgType || asgEnv || asgTier || asgLast || asgDest) || heatHour != null;
  const resetFilters = () => {
    setAsgQ('');
    setAsgType('');
    setAsgEnv('');
    setAsgTier('');
    setAsgLast('');
    setAsgDest('');
    setHeatHour(null);
  };

  // The one lever: shift a backup's start time, then refresh the config + the heatmap above.
  const saveStartTime = async (e: AssignableEntity, hour: number, minute: number) => {
    await assignmentApi.setStartTime(e.entityType, e.entityId, hour, minute);
    await refreshSchedules();
  };

  // Spec 214 T035 (Clarify Q1): tier + retention are SETTABLE per row here — the operational
  // surface the Settings copy always pointed at. Same per-source endpoints as the Catalog editors.
  const setRowTier = async (e: AssignableEntity, tier: string) => {
    if (!tier || e.entityType === 'workspace') return;
    await tiersApi.setEntityTier(e.entityType as 'lakehouse' | 'warehouse', e.entityId, tier);
    await qc.invalidateQueries({ queryKey: ['backup', 'backup-configs'] });
    await qc.invalidateQueries({ queryKey: ['backup', 'dash'] });
  };
  const setRowRetention = async (e: AssignableEntity, policyId: string | null) => {
    if (e.entityType === 'workspace') return;
    await retentionApi.setEntityRetention(e.entityType as 'lakehouse' | 'warehouse', e.entityId, policyId);
    await qc.invalidateQueries({ queryKey: ['backup', 'backup-configs'] });
  };

  // "Back up now" runs a FULL backup (owner: one button, always full).
  const backupNow = async (e: AssignableEntity) => {
    const k = key(e);
    setTrigger((t) => ({ ...t, [k]: { status: 'running' } }));
    // Pushes to the global backup-activity store → live status in the universal
    // Activity drawer (header), so progress is not trapped in this tab.
    const r = await triggerBackup({ entityType: e.entityType, entityId: e.entityId, name: e.name }, 'full');
    setTrigger((t) => ({
      ...t,
      [k]: r.launched ? { status: 'launched', msg: r.executionName } : { status: 'failed', msg: r.reason },
    }));
  };

  // Move a backup to a DIFFERENT plan (attach = move; detaches from the old plan). Its schedule +
  // destination then follow the new plan (rematerialized server-side, FR-002); retention resolves
  // source → plan → default. Spec 214 FR-029: failures are surfaced, not swallowed.
  const [changeError, setChangeError] = useState<string | null>(null);
  const changeEntityPlan = async (e: AssignableEntity, newPlanId: string) => {
    setBusy(true);
    setChangeError(null);
    try {
      await plansApi.attachTarget(newPlanId, e.entityType, e.entityId);
      const plan = planById.get(newPlanId);
      if (plan?.storageAccountId) {
        await assignmentApi.bulkAssign([{ entityType: e.entityType, entityId: e.entityId }], plan.storageAccountId, true);
      }
      await assignmentApi.ensureSchedules().catch(() => undefined);
      qc.invalidateQueries({ queryKey: ['backup', 'assignable-entities'] });
      await refreshSchedules();
      setChangeFor(null);
    } catch (err) {
      setChangeError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  if (entities.isLoading || storage.isLoading) return <LoadingState rows={5} />;
  if (entities.isError) return <ErrorState onRetry={() => entities.refetch()} />;

  return (
    <div className="space-y-6">
      {/* Schedule load across the week — spot clustering before it floods Fabric. Click a square
          to drill the Backups list into the backups that run in that hour. */}
      <Card>
        <CardHeader title="Schedule load" subtitle="When backups are scheduled to run across the week — click a square to see those backups below" />
        <CardBody>
          <ScheduleSpreadHeatmap
            selectedHour={heatHour}
            onCellClick={(_d, h) => setHeatHour((cur) => (cur === h ? null : h))}
          />
        </CardBody>
      </Card>

      {/* Backups: per-backup config — destination, schedule, last run + inline actions */}
      <Card>
        <CardHeader title="Backups" subtitle="Each backup's destination, schedule and last run — manage in place" />
        <CardBody>
          {assigned.length === 0 ? (
            <EmptyState title="No assigned entities yet" />
          ) : (
            <>
              {heatHour != null && (
                <div className="mb-3 flex items-center gap-2 rounded-md border border-[var(--color-accent)] bg-[var(--color-bg-subtle)] px-3 py-2 text-sm">
                  <Clock className="h-4 w-4 text-[var(--color-accent)]" />
                  <span className="text-[var(--color-text-primary)]">
                    Showing backups scheduled at {String(heatHour).padStart(2, '0')}:00 ({shownAssigned.length})
                  </span>
                  <button
                    type="button"
                    onClick={() => setHeatHour(null)}
                    className="ml-1 inline-flex items-center gap-1 text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                  >
                    <X className="h-3.5 w-3.5" /> clear
                  </button>
                </div>
              )}
              <ListFilterBar
                query={asgQ}
                onQuery={setAsgQ}
                placeholder="Search assigned…"
                count={shownAssigned.length}
                total={assigned.length}
              >
                <Select
                  value={asgType}
                  onChange={(e) => setAsgType(e.target.value)}
                  options={[
                    { value: '', label: 'All types' },
                    { value: 'lakehouse', label: 'Lakehouse' },
                    { value: 'warehouse', label: 'Warehouse' },
                    { value: 'workspace', label: 'Workspace' },
                  ]}
                />
                <Select
                  value={asgEnv}
                  onChange={(e) => setAsgEnv(e.target.value)}
                  options={[
                    { value: '', label: 'All envs' },
                    { value: 'prod', label: 'prod' },
                    { value: 'uat', label: 'uat' },
                    { value: 'dev', label: 'dev' },
                    { value: 'test', label: 'test' },
                  ]}
                />
                <Select
                  value={asgTier}
                  onChange={(e) => setAsgTier(e.target.value)}
                  options={[
                    { value: '', label: 'All tiers' },
                    ...PROTECTION_TIERS.map((t) => ({ value: t, label: `${tierIcon(t)} ${t}` })),
                  ]}
                />
                <Select
                  value={asgLast}
                  onChange={(e) => setAsgLast(e.target.value)}
                  options={[
                    { value: '', label: 'Any last backup' },
                    { value: 'backed', label: 'Backed up' },
                    { value: 'never', label: 'Never backed up' },
                  ]}
                />
                {destOptions.length > 1 && (
                  <Select
                    value={asgDest}
                    onChange={(e) => setAsgDest(e.target.value)}
                    options={[
                      { value: '', label: 'All destinations' },
                      ...destOptions.map((d) => ({ value: d, label: d })),
                    ]}
                  />
                )}
                <Button variant="ghost" size="sm" onClick={resetFilters} disabled={!filtersActive} title="Clear all filters">
                  <X className="h-4 w-4" /> Reset
                </Button>
                <Button variant="outline" size="sm" onClick={refreshBackups} disabled={refreshing} title="Refresh last-backup status">
                  <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
                </Button>
                {/* Spec 208 — change history (always available; not selection-dependent) */}
                <BulkStorageRpoActions targets={[]} canManage={canManage} showBulk={false} showHistory />
              </ListFilterBar>
              {/* Spec 208 — bulk actions on SELECTED backups (multi-select, mirrors the Plans tab). */}
              {canManage && selectedTargets.length > 0 && (
                <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-[var(--color-accent)] bg-[var(--color-bg-subtle)] px-3 py-2">
                  <span className="text-sm font-medium text-[var(--color-text-primary)]">{selectedTargets.length} selected</span>
                  <span className="grow" />
                  <BulkStorageRpoActions
                    targets={selectedTargets}
                    canManage={canManage}
                    showHistory={false}
                    onApplied={() => {
                      refreshBackups();
                      setSelected(new Set());
                    }}
                  />
                  <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                    Clear
                  </Button>
                </div>
              )}
            <DataTable<AssignableEntity>
              minWidth="62rem"
              columns={[
                {
                  key: 'select',
                  header: canManage ? <Checkbox checked={allShownSelected} onChange={toggleAllShown} aria-label="Select all shown" /> : '',
                  width: '2.5rem',
                  sortable: false,
                  // Workspace rows are read-only for storage (destination is the plan's) — not selectable
                  // for the bulk reassign / RPO actions, which only apply to lakehouse/warehouse.
                  render: (e) =>
                    canManage && e.entityType !== 'workspace' ? (
                      <span onClick={(ev) => ev.stopPropagation()}>
                        <Checkbox checked={selected.has(key(e))} onChange={() => toggleSel(key(e))} aria-label={`Select ${e.name ?? e.entityId}`} />
                      </span>
                    ) : null,
                },
                {
                  key: 'name',
                  header: 'Entity',
                  width: '15rem',
                  render: (e) => (
                    <span className="block min-w-0">
                      <span className="block font-medium text-[var(--color-text-primary)]">{e.name ?? e.entityId}</span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
                        <Badge variant="default">{e.entityType}</Badge>
                        <span className="truncate">{e.workspaceName ?? '—'}</span>
                        {e.envHint && <Badge variant={envVariant[e.envHint] ?? 'default'}>{e.envHint}</Badge>}
                      </span>
                    </span>
                  ),
                },
                {
                  key: 'plan',
                  header: 'Plan',
                  width: '9rem',
                  wrap: true,
                  sortable: false,
                  render: (e) => {
                    const p = cfg(e)?.planName;
                    return p ? (
                      <span className="block text-sm text-[var(--color-text-primary)]">{p}</span>
                    ) : (
                      <span className="text-xs text-[var(--color-text-secondary)]">—</span>
                    );
                  },
                },
                {
                  key: 'storageAccountName',
                  header: 'Destination',
                  width: '10rem',
                  wrap: true,
                  // Same rule as the destination dropdowns (owner, 2026-08-10): the ACCOUNT NAME
                  // is the identity, the description sits beside it — here on a second line,
                  // because a table cell has the vertical room a <select> option does not.
                  render: (e) => {
                    const name = e.storageAccountName ?? e.storageAccountId;
                    if (!name) return <span className="text-[var(--color-text-secondary)]">—</span>;
                    const { subtitle } = storageAccountParts({
                      name: e.storageAccountName ?? '',
                      displayName: e.storageAccountDisplayName ?? null,
                    });
                    return (
                      <span className="block min-w-0">
                        <span className="block text-[var(--color-text-primary)]">{name}</span>
                        {subtitle && (
                          <span className="mt-0.5 block text-xs text-[var(--color-text-secondary)]">{subtitle}</span>
                        )}
                      </span>
                    );
                  },
                },
                {
                  key: 'tier',
                  header: 'Tier',
                  width: '8rem',
                  sortable: false,
                  // Spec 214 T035 (Clarify Q1): per-row SETTER — the Settings copy always said
                  // "assign per source on the Backups tab"; the column was read-only until now.
                  // Workspace rows keep the badge (tier lives on lakehouses/warehouses only).
                  render: (e) =>
                    canManage && e.entityType !== 'workspace' ? (
                      <Select
                        value={cfg(e)?.protectionTier ?? ''}
                        onChange={(ev) => void setRowTier(e, ev.target.value)}
                        options={[
                          { value: '', label: '— none —' },
                          ...PROTECTION_TIERS.map((t) => ({ value: t, label: t })),
                        ]}
                      />
                    ) : (
                      <ProtectionTierBadge tier={cfg(e)?.protectionTier} size="sm" />
                    ),
                },
                {
                  key: 'retention',
                  header: 'Retention',
                  width: '11rem',
                  wrap: true,
                  sortable: false,
                  // Spec 214 T035: per-row source-policy setter; blank = resolve plan → default (D6).
                  render: (e) =>
                    canManage && e.entityType !== 'workspace' ? (
                      <span className="block" title={cfg(e)?.retentionName ?? ''}>
                        <Select
                          value={cfg(e)?.retentionAssignedAtSource ? cfg(e)?.retentionPolicyId ?? '' : ''}
                          onChange={(ev) => void setRowRetention(e, ev.target.value || null)}
                          options={[
                            { value: '', label: '— plan / default —' },
                            ...(retentionQ.data ?? []).map((p) => ({ value: p.id, label: p.name })),
                          ]}
                        />
                        <span className="mt-0.5 block text-xs text-[var(--color-text-secondary)]">
                          {retentionShort(policyById.get(cfg(e)?.retentionPolicyId ?? ''))}
                        </span>
                      </span>
                    ) : (
                      <span
                        className="block text-sm text-[var(--color-text-secondary)]"
                        title={cfg(e)?.retentionName ?? ''}
                      >
                        {retentionShort(policyById.get(cfg(e)?.retentionPolicyId ?? ''))}
                      </span>
                    ),
                },
                {
                  key: 'schedule',
                  header: 'Schedule · start time',
                  width: '11rem',
                  sortable: false,
                  render: (e) => (
                    <StartTimeCell cfg={cfg(e)} disabled={!canManage} onSave={(h, m) => saveStartTime(e, h, m)} />
                  ),
                },
                {
                  key: 'last',
                  header: 'Last backup',
                  width: '7rem',
                  sortable: false,
                  render: (e) => {
                    const c = cfg(e);
                    return c?.lastBackupAt ? (
                      <span className="text-sm text-[var(--color-text-primary)]">{fmtWhen(c.lastBackupAt)}</span>
                    ) : (
                      <span className="text-xs text-[var(--color-text-secondary)]">never</span>
                    );
                  },
                },
                {
                  key: 'actions',
                  header: 'Actions',
                  sortable: false,
                  width: '9.5rem',
                  wrap: true,
                  render: (e) => {
                    const running = trigger[key(e)]?.status === 'running';
                    return canManage ? (
                      <div className="flex flex-col items-stretch gap-1.5">
                        <Button variant="primary" size="sm" onClick={() => backupNow(e)} disabled={running}>
                          <Play className="h-4 w-4" /> {running ? 'Starting…' : 'Back up now'}
                        </Button>
                        {/* Change-plan moves storage/retention/schedule between LH/WH plans; workspace
                            backups are managed via their plan on the Plans tab. */}
                        {e.entityType !== 'workspace' && (
                          <Button variant="outline" size="sm" onClick={() => setChangeFor(e)} disabled={busy}>
                            <ArrowLeftRight className="h-4 w-4" /> Change plan
                          </Button>
                        )}
                        {trigger[key(e)]?.status === 'launched' && <Badge variant="success">started</Badge>}
                        {trigger[key(e)]?.status === 'failed' && (
                          <span className="text-xs text-[var(--color-status-warning)]">{trigger[key(e)]?.msg}</span>
                        )}
                      </div>
                    ) : (
                      <span>—</span>
                    );
                  },
                },
              ]}
              rows={shownAssigned}
              rowKey={(e) => key(e)}
            />
            </>
          )}
        </CardBody>
      </Card>

      {/* Change-plan modal — move this backup to a different plan (its schedule + destination
          follow the new plan, rematerialized immediately server-side per Spec 214 FR-002;
          retention resolves source → plan → default). */}
      <Modal
        isOpen={!!changeFor}
        onClose={() => setChangeFor(null)}
        title={`Change plan — ${changeFor?.name ?? changeFor?.entityId ?? ''}`}
        size="lg"
      >
        <p className="mb-3 text-sm text-[var(--color-text-secondary)]">
          Moving this backup to another plan switches its <strong>schedule and destination</strong> to that
          plan&apos;s immediately; retention resolves <strong>source policy → plan policy → default</strong>.
          The other backups on the current plan are unaffected.
        </p>
        {changeError && (
          <p className="mb-3 rounded-md border border-[var(--color-status-danger)] px-3 py-2 text-sm text-[var(--color-status-danger)]">
            Change failed: {changeError}
          </p>
        )}
        {(plansQ.data ?? []).filter((p) => p.status !== 'archived').length === 0 ? (
          <p className="px-1 py-2 text-sm text-[var(--color-text-secondary)]">No plans available — create one in the Plans tab first.</p>
        ) : (
          <div className="max-h-80 space-y-1 overflow-auto">
            {(plansQ.data ?? [])
              .filter((p) => p.status !== 'archived')
              .map((p) => (
                <button
                  key={p.id}
                  disabled={busy}
                  onClick={() => changeFor && changeEntityPlan(changeFor, p.id)}
                  className="flex w-full flex-col gap-0.5 rounded border border-[var(--color-border)] px-3 py-2 text-left hover:bg-[var(--color-bg-subtle)] disabled:opacity-60"
                >
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[var(--color-text-primary)]">{p.name}</span>
                    <Badge variant="default">{p.status}</Badge>
                  </span>
                  <span className="text-xs text-[var(--color-text-secondary)]">
                    Destination: {p.storageAccountId ? storageName(p.storageAccountId) : '—'}
                  </span>
                </button>
              ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StorageAssignment;
